import { useEffect, useState } from "react";
import { api, notifyError } from "../../lib/api";
import { fmtMoney, CURRENCY_OPTIONS } from "../../lib/currency";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Package,
  Trash2,
  Loader2,
  Pencil,
  Infinity as InfinityIcon,
  Star,
} from "lucide-react";

const empty = {
  name: "",
  price_try: 0,
  currency: "TRY",
  max_customers: 0,
  max_users: 0,
  period_days: 30,
  is_active: true,
  paytr_max_installment: 0,
  // Modül yetkilendirmesi (şartname #10)
  sales_stock_enabled: false,
};

const INSTALLMENT_OPTIONS = [
  { value: 0, label: "Sınırsız (Banka maksimumu)" },
  { value: 1, label: "Sadece tek çekim (Taksit kapalı)" },
  { value: 2, label: "Maksimum 2 taksit" },
  { value: 3, label: "Maksimum 3 taksit" },
  { value: 4, label: "Maksimum 4 taksit" },
  { value: 5, label: "Maksimum 5 taksit" },
  { value: 6, label: "Maksimum 6 taksit" },
  { value: 7, label: "Maksimum 7 taksit" },
  { value: 8, label: "Maksimum 8 taksit" },
  { value: 9, label: "Maksimum 9 taksit" },
  { value: 10, label: "Maksimum 10 taksit" },
  { value: 11, label: "Maksimum 11 taksit" },
  { value: 12, label: "Maksimum 12 taksit" },
];

function installmentLabel(v) {
  const opt = INSTALLMENT_OPTIONS.find((o) => o.value === (v ?? 0));
  return opt ? opt.label : "Sınırsız";
}

function fmtTRY(n) {
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
}

function fmtPlanPrice(p) {
  return fmtMoney(p.price_try, p.currency || "TRY", { maximumFractionDigits: 0 });
}

export default function MasterPlans() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/plans");
      setList(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const startEdit = (p) => {
    setForm({
      name: p.name,
      price_try: p.price_try,
      currency: p.currency || "TRY",
      max_customers: p.max_customers,
      max_users: p.max_users,
      period_days: p.period_days,
      is_active: p.is_active,
      paytr_max_installment: p.paytr_max_installment ?? 0,
      sales_stock_enabled: !!p.sales_stock_enabled,
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_try: Number(form.price_try) || 0,
        max_customers: Number(form.max_customers) || 0,
        max_users: Number(form.max_users) || 0,
        period_days: Number(form.period_days) || 0,
        paytr_max_installment: Number(form.paytr_max_installment) || 0,
        sales_stock_enabled: !!form.sales_stock_enabled,
      };
      if (editingId) {
        await api.put(`/master/plans/${editingId}`, payload);
        toast.success("Paket güncellendi");
      } else {
        await api.post("/master/plans", payload);
        toast.success("Paket eklendi");
      }
      setOpen(false);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/master/plans/${deleteId}`);
      toast.success("Paket silindi");
      setDeleteId(null);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="master-plans-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Abonelik</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Paketler</h1>
          <p className="text-sm text-muted-foreground mt-2">
            İşletmeler için paket oluşturun. Müşteri/kullanıcı limiti, süresi ve fiyatını siz belirleyin.
          </p>
        </div>
        <Button onClick={startCreate} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="add-plan-btn">
          <Plus className="h-4 w-4 mr-2" /> Yeni Paket
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <Package className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">Henüz paket yok</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <Card
              key={p.id}
              className={`rounded-sm p-6 border ${p.is_default ? "border-amber-500 bg-amber-500/5" : "border-border"}`}
              data-testid={`plan-card-${p.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-amber-600" />
                    {p.is_default && <Badge className="rounded-sm bg-amber-500 text-foreground hover:bg-amber-500 text-[10px]"><Star className="h-3 w-3 mr-1" /> Varsayılan</Badge>}
                    {!p.is_active && <Badge variant="outline" className="rounded-sm text-[10px]">Pasif</Badge>}
                  </div>
                  <div className="font-display font-black text-2xl tracking-tighter mt-2">{p.name}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(p)} className="p-2 hover:bg-muted rounded-sm" data-testid={`edit-plan-${p.id}`} aria-label="Düzenle">
                    <Pencil className="h-4 w-4" />
                  </button>
                  {!p.is_default && (
                    <button onClick={() => setDeleteId(p.id)} className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-sm" data-testid={`delete-plan-${p.id}`} aria-label="Sil">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="font-display font-black text-3xl tracking-tighter mt-4">
                {p.price_try > 0 ? fmtPlanPrice(p) : <span className="text-amber-600">Ücretsiz</span>}
                {p.price_try > 0 && p.period_days > 0 && (
                  <span className="text-sm text-muted-foreground ml-1 font-normal">/{p.period_days}gün</span>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
                <Row k="Müşteri Limiti" v={p.max_customers > 0 ? p.max_customers : <span className="inline-flex items-center gap-1"><InfinityIcon className="h-4 w-4" /> Sınırsız</span>} />
                <Row k="Kullanıcı Limiti" v={p.max_users > 0 ? p.max_users : <span className="inline-flex items-center gap-1"><InfinityIcon className="h-4 w-4" /> Sınırsız</span>} />
                <Row k="Süre" v={p.period_days > 0 ? `${p.period_days} gün` : "Sınırsız"} />
                {p.price_try > 0 && <Row k="PayTR Taksit" v={installmentLabel(p.paytr_max_installment)} />}
                <Row
                  k="Satış/Stok Modülü"
                  v={
                    p.sales_stock_enabled ? (
                      <Badge className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-600 text-[10px]" data-testid={`plan-sales-stock-${p.id}`}>
                        Aktif
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-sm text-[10px]" data-testid={`plan-sales-stock-${p.id}`}>
                        Pasif
                      </Badge>
                    )
                  }
                />
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editingId ? "Paket Düzenle" : "Yeni Paket"}</DialogTitle>
            <DialogDescription>İsim, fiyat ve limit bilgilerini girin. 0 = Sınırsız anlamına gelir.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="plan-form">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Paket Adı *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="plan-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Fiyat</Label>
                <Input type="number" inputMode="decimal" min="0" value={form.price_try} onChange={(e) => setForm({ ...form, price_try: e.target.value })} className="rounded-sm" data-testid="plan-price" />
                <p className="text-xs text-muted-foreground">0 = Ücretsiz paket</p>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="plan-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`currency-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Müşteri Limiti</Label>
                <Input type="number" inputMode="numeric" min="0" value={form.max_customers} onChange={(e) => setForm({ ...form, max_customers: e.target.value })} className="rounded-sm" data-testid="plan-max-customers" />
                <p className="text-[10px] text-muted-foreground">0 = sınırsız</p>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Kullanıcı Limiti</Label>
                <Input type="number" inputMode="numeric" min="0" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} className="rounded-sm" data-testid="plan-max-users" />
                <p className="text-[10px] text-muted-foreground">0 = sınırsız</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Süre (Gün)</Label>
              <Input type="number" inputMode="numeric" min="0" value={form.period_days} onChange={(e) => setForm({ ...form, period_days: e.target.value })} className="rounded-sm" data-testid="plan-period" />
              <p className="text-xs text-muted-foreground">30 = aylık, 365 = yıllık, 0 = sınırsız (lifetime)</p>
            </div>
            <div className="space-y-1.5 border-t border-border pt-3">
              <Label className="label-eyebrow">PayTR Taksit</Label>
              <Select
                value={String(form.paytr_max_installment ?? 0)}
                onValueChange={(v) => setForm({ ...form, paytr_max_installment: Number(v) })}
              >
                <SelectTrigger className="rounded-sm" data-testid="plan-paytr-installment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSTALLMENT_OPTIONS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={String(o.value)}
                      data-testid={`paytr-installment-${o.value}`}
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                Müşteri PayTR ödeme sayfasında seçili taksit seçeneklerini görür. Sadece PayTR sağlayıcısında geçerli; Stripe'ı etkilemez.
              </p>
            </div>

            {/* Modül yetkilendirmesi — şartname #10 */}
            <div className="border-t border-border pt-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow">Satış/Stok Modülü</Label>
                <Switch
                  checked={!!form.sales_stock_enabled}
                  onCheckedChange={(v) => setForm({ ...form, sales_stock_enabled: v })}
                  data-testid="plan-sales-stock-toggle"
                />
              </div>
              <p className="text-[10px] text-muted-foreground">
                Aktif ise bu pakete sahip firmalar Satış/Stok modülünü kullanabilir.
                Pasif ise menü gösterilmez ve API erişimi engellenir.
              </p>
            </div>

            <div className="flex items-center justify-between border-t border-border pt-3">
              <Label className="label-eyebrow">Aktif</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="plan-active" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">İptal</Button>
              <Button type="submit" disabled={saving} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="plan-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Güncelle" : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Paket silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu pakete bağlı işletmeler otomatik olarak Free pakete taşınacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-semibold">{v}</span>
    </div>
  );
}
